import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { loadState } from "../../stores/store";
import { createApplication } from '../../api/application';
import { getAllEvents, createEvent, updateEvent, deleteEvent } from '../../api/event';
import { searchGroupsByUser } from '../../api/group';
import { searchContestants } from '../../api/contestant';
import { Button, Card, Flex, Space, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import EventTable from '../../components/Events/eventTable';
import EventForm from '../../components/Events/eventForm';
import EventEditForm from '../../components/Events/eventEditForm.js';
import ApplicationForm from '../../components/Events/applicationForm';
import { openNotificationWithIcon } from "../../utils/notificationUtils";
import { useSettings } from "../../contexts/settingsContext";

const { Title } = Typography;

const EventPage = () => {
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [error, setError] = useState(null);
    const [groups, setGroups] = useState([]);
    const [contestants, setContestants] = useState([]);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [isEventPopupVisible, setIsEventPopupVisible] = useState(false);
    const [isEventEditPopupVisible, setIsEventEditPopupVisible] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedContestant, setSelectedContestant] = useState(null);

    const navigate = useNavigate();
    const { state: settings } = useSettings();

    const auth = useMemo(() => loadState(), []);

    const user = auth.user;
    const userRole = user.role;

    const fetchEventsAndGroups = useCallback(async () => {
        try {
            setLoading(true);
            const [eventsData, groupsData] = await Promise.all([
                getAllEvents(),
                searchGroupsByUser().catch(() => [])
            ]);
            setEvents(eventsData);
            setGroups(groupsData || []);
        } catch (err) {
            console.error("Error fetching data", err);
            setError("Failed to load data. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchContestants = useCallback(async () => {
        try {
            setLoading(true);
            const contestantsData = await searchContestants();
            setContestants(contestantsData || []);
        } catch (err) {
            console.error("Error fetching contestants", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEventsAndGroups();
    }, [fetchEventsAndGroups]);

    const openPopup = useCallback(async (event) => {
        setSelectedEvent(event);
        if (userRole === 'contestant' && event.solo_bool) {
            try {
                const contestantsData = await searchContestants();
                const contestant = contestantsData ? contestantsData[0] : null;
                if (contestant) {
                    await createApplication({
                        event: event._id,
                        created_by: user,
                        contestant: contestant._id,
                        round: settings.selectedRound,
                        province: user.activeProvince
                    });
                    openNotificationWithIcon('success', 'Application created successfully!', 'You have been successfully applied to the selected event');
                } else {
                    throw new Error("No contestant found.");
                }
            } catch (err) {
                console.error("Error creating application", err);
                openNotificationWithIcon('error', 'Error', `Failed to create application. ${err.message}`);
            }
        } else {
            if (event.solo_bool) {
                await fetchContestants();
            }
            setIsPopupVisible(true);
        }
    }, [userRole, user, settings.selectedRound, fetchContestants]);

    const closePopup = useCallback(() => setIsPopupVisible(false), []);
    const openEventPopup = useCallback(() => setIsEventPopupVisible(true), []);
    const closeEventPopup = useCallback(() => setIsEventPopupVisible(false), []);
    const openEventEditPopup = useCallback(() => setIsEventEditPopupVisible(true), []);
    const closeEventEditPopup = useCallback(() => {
        setIsEventEditPopupVisible(false);
        setSelectedEvent(null);
    }, []);

    const handleFormSubmit = useCallback(async (values) => {
        try {
            setLoading(true);
            if (!selectedEvent) throw new Error("No event selected.");
            const applicationData = {
                event: selectedEvent._id,
                created_by: user._id,
                round: settings.selectedRound
            };
            if (selectedEvent.solo_bool && selectedContestant) {
                applicationData.contestant = selectedContestant;
            } else if (selectedGroup) {
                applicationData.group = selectedGroup;
            } else {
                throw new Error("Please select a valid group or contestant.");
            }
            await createApplication(applicationData);
            openNotificationWithIcon('success', 'Application created successfully!', 'The contestant has been successfully applied to the selected events');
            closePopup();
        } catch (err) {
            console.error("Error creating application", err);
            openNotificationWithIcon('error', 'Error', `Failed to create application. ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [selectedEvent, selectedContestant, selectedGroup, user, settings.selectedRound, closePopup]);

    const handleNewEventSubmit = useCallback(async (values) => {
        try {
            setLoading(true);
            await createEvent(values);
            openNotificationWithIcon('success', 'Event Created', 'The event has been successfully created!');
            closeEventPopup();
            fetchEventsAndGroups();
        } catch (err) {
            console.error("Error creating event", err);
            openNotificationWithIcon('error', 'Event Creation Failed', `Failed to create event. ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchEventsAndGroups, closeEventPopup]);

    const handleEditEventSubmit = useCallback(async (values) => {
        try {
            setLoading(true);
            if (!selectedEvent) throw new Error("No event selected.");
            await updateEvent(selectedEvent._id, values);
            openNotificationWithIcon('success', 'Event Updated', 'The event has been successfully updated!');
            closeEventEditPopup();
            fetchEventsAndGroups();
        } catch (err) {
            console.error("Error updating event", err);
            openNotificationWithIcon('error', 'Event Update Failed', `Failed to update event. ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [selectedEvent, fetchEventsAndGroups, closeEventEditPopup]);

    const handleDeleteEvent = useCallback(async (eventId) => {
        try {
            setLoading(true);
            await deleteEvent(eventId);
            openNotificationWithIcon('success', 'Event Deleted', 'The event has been successfully deleted!');
            fetchEventsAndGroups();
        } catch (err) {
            console.error("Error deleting event", err);
            openNotificationWithIcon('error', 'Event Deletion Failed', `Failed to delete event. ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchEventsAndGroups]);

    const handleEditEvent = useCallback((event) => {
        setSelectedEvent(event);
        openEventEditPopup();
    }, [openEventEditPopup]);

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div>
            <Flex style={{ marginBottom: '15px' }} className='dashboard_page_header_area' align="center" justify="space-between">
                <Title className='dashboard_page_title' level={4} style={{ margin: 0 }}>Events</Title>
                    <Space className='dashboard_page_title_actions'>
                        {userRole === 'admin' && (
                            <Space>
                                <Button type="primary" onClick={openEventPopup}>
                                    <PlusOutlined />
                                    Create Event
                                </Button>
                            </Space>
                        )}
                    </Space>
            </Flex>
            <Card bordered={false}>
                <EventTable
                    events={events}
                    loading={loading}
                    openPopup={openPopup}
                    userRole={userRole}
                    handleDeleteEvent={handleDeleteEvent}
                    handleEditEvent={handleEditEvent}
                />
            </Card>
            <ApplicationForm
                visible={isPopupVisible}
                onCancel={closePopup}
                onSubmit={handleFormSubmit}
                selectedEvent={selectedEvent}
                contestants={contestants}
                groups={groups}
                handleContestantChange={setSelectedContestant}
                handleGroupChange={setSelectedGroup}
            />
            <EventForm
                visible={isEventPopupVisible}
                onCancel={closeEventPopup}
                onSubmit={handleNewEventSubmit}
            />
            <EventEditForm
                visible={isEventEditPopupVisible}
                onCancel={closeEventEditPopup}
                onSubmit={handleEditEventSubmit}
                initialValues={selectedEvent}
            />
        </div>
    );
};

export default EventPage;  